<script setup>
import { ref, watch } from 'vue';
import UserSelectField from '~/components/form/UserSelectField.vue';
import { useMessaging } from '~/composables/useMessaging';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue', 'created']);

// Get messaging composable
const { createConversation } = useMessaging();

// Form state
const formData = ref({
  conversationType: 1, // 1 = OneToOne, 2 = Group
  title: '',
  participantUserIds: []
});

// Loading and error states
const formPending = ref(false);
const formErrors = ref({});

// Watch conversationType changes to handle single/multiple conversion
watch(() => formData.value.conversationType, (newType, oldType) => {
  if (newType === 1) {
    // Switching to one-to-one
    if (Array.isArray(formData.value.participantUserIds) && formData.value.participantUserIds.length > 1) {
      // Keep only the first user when switching to one-to-one
      formData.value.participantUserIds = formData.value.participantUserIds[0];
    } else if (Array.isArray(formData.value.participantUserIds) && formData.value.participantUserIds.length === 1) {
      // Convert single-item array to string
      formData.value.participantUserIds = formData.value.participantUserIds[0];
    }
  } else if (newType === 2 && oldType === 1) {
    // Switching from one-to-one to group
    if (typeof formData.value.participantUserIds === 'string') {
      // Convert single string ID to array
      formData.value.participantUserIds = [formData.value.participantUserIds];
    } else if (!Array.isArray(formData.value.participantUserIds)) {
      // Ensure it's an array
      formData.value.participantUserIds = [];
    }
  }
});

const handleCreate = async () => {
  console.log('handleCreate called');
  formPending.value = true;
  formErrors.value = {};

  try {
    // Client-side validation
    const participantIds = Array.isArray(formData.value.participantUserIds)
      ? formData.value.participantUserIds
      : (formData.value.participantUserIds ? [formData.value.participantUserIds] : []);

    console.log('Participant IDs:', participantIds);

    if (participantIds.length === 0) {
      console.log('Validation failed: no participants');
      formErrors.value.participantUserIds = ['Select at least one participant'];
      return;
    }

    if (formData.value.conversationType === 2 && !formData.value.title?.trim()) {
      console.log('Validation failed: no title for group');
      formErrors.value.title = ['Group name is required'];
      return;
    }

    console.log('Calling createConversation...');
    // Create the conversation
    const conversation = await createConversation(
      participantIds,
      formData.value.conversationType,
      formData.value.conversationType === 2 ? formData.value.title.trim() : null
    );

    console.log('createConversation returned:', conversation);

    // Success - emit created event and close dialog
    console.log('Emitting created event...');
    emit('created', conversation);
    console.log('Emitting update:modelValue to close dialog...');
    emit('update:modelValue', false);
    console.log('Emits complete');
  } catch (error) {
    console.error('Error creating conversation:', error);
    // Show error in dialog (CrudErrorDisplay expects 'global' key)
    formErrors.value.global = [error.message || 'Failed to create conversation'];
  } finally {
    console.log('Setting formPending to false');
    formPending.value = false;
    console.log('formPending is now:', formPending.value);
  }
};

// Reset form when dialog closes
watch(() => props.modelValue, (isOpen) => {
  if (!isOpen) {
    formData.value = {
      conversationType: 1,
      title: '',
      participantUserIds: []
    };
    formErrors.value = {};
  }
});
</script>

<template>
  <crud-form-dialog
    :dialog="modelValue"
    @update:dialog="$emit('update:modelValue', $event)"
    v-model="formData"
    :form-pending="formPending"
    :form-errors="formErrors"
    :save-action="handleCreate"
    action="create"
    create-title="New Conversation"
    btn-text="Create"
    btn-icon="mdi-plus"
  >
    <template #default="{ item, errors }">
      <!-- Conversation type -->
      <v-radio-group
        v-model="item.conversationType"
        label="Conversation Type"
        inline
        class="mb-4"
      >
        <v-radio label="One-to-One" :value="1" />
        <v-radio label="Group" :value="2" />
      </v-radio-group>

      <!-- Group name (only for group conversations) -->
      <template v-if="item.conversationType === 2">
        <div class="tl-label mb-2">Group Name</div>
        <v-text-field
          v-model="item.title"
          variant="outlined"
          density="comfortable"
          class="mb-4"
          :error="errors.title != null"
          :error-messages="errors.title"
        />
      </template>

      <!-- User selection -->
      <user-select-field
        v-model="item.participantUserIds"
        :label="item.conversationType === 1 ? 'Select Participant' : 'Add Participants'"
        :multiple="item.conversationType === 2"
        :error="errors.participantUserIds != null"
        :error-messages="errors.participantUserIds"
        :hint="item.conversationType === 1 ? 'Select one user for a direct conversation' : 'Search and select users to add to this group conversation'"
      />
    </template>
  </crud-form-dialog>
</template>
